import {
  VehicleFuelType,
  VehicleOwner,
  VehicleStatus,
} from "@1games-ru/region-shared/dist/enums/vehicle/vehicle";
import { VehicleUpgrade } from "@1games-ru/region-shared/dist/types/vehicle/vehicle";
import { VehicleDetailsDto } from "@1games-ru/region-shared/dist/types/vehicle/vehicle-b";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import VehicleCdn from "@/core/cdn/vehicle/vehicle-cdn";
import { MAX_ACCELERATION, MAX_HANDLING, MAX_SPEED } from "@/core/constants/vehicleLimits";
import {
  BrakesIcon,
  CrossIcon,
  ElectricIcon,
  EngineIcon,
  FuelIcon,
  TurboIcon,
} from "@/shared/icons";
import { BaseIconProps } from "@/shared/icons/BaseIcon";
import { Button, ProgressBar, VehicleStand } from "@/shared/ui";
import PersonalDataCard from "@/shared/widgets/PersonalDataCard";

type Props = {
  vehicle: VehicleDetailsDto;
  onCharacterDetach?: (vehicleId: number, characterId: number) => void;
  isDetaching: boolean;
};

function FuelTypeLabel({ fuelType }: { fuelType: VehicleFuelType }) {
  const Icon = fuelType === VehicleFuelType.Electric ? ElectricIcon : FuelIcon;
  return <Icon size={16} />;
}

const upgradeIcon: Record<keyof VehicleUpgrade, FC<BaseIconProps>> = {
  turbo: TurboIcon,
  engine: EngineIcon,
  brakes: BrakesIcon,
};

export default function VehicleDetailsDepricated({
  vehicle,
  onCharacterDetach,
  isDetaching,
}: Props) {
  const { t } = useTranslation("estate");
  const {
    name,
    fuel,
    status,
    model,
    specification,
    plateNumber,
    mileage,
    condition,
    maxInventoryWeight,
    owner,
    relation,
    shared,
    upgrade,
    vehicleId,
    vinNumber,
  } = vehicle;

  const speedPercent = Math.ceil((specification.speed / MAX_SPEED) * 100);
  const accelerationPercent = Math.ceil((specification.acceleration / MAX_ACCELERATION) * 100);
  const handlingPercent = Math.ceil((specification.handling / MAX_HANDLING) * 100);

  return (
    <div className="flex flex-col gap-7.5">
      <div>
        <div className="flex items-start gap-[1px] h-[18.3125rem] rounded-t-2xl overflow-clip">
          <div
            className={clsx(
              "w-[24rem] h-full",
              "relative flex items-center justify-center bg-gradient-to-br from-accent-muted/20 via-80% via-transparent to-transpaent",
            )}
          >
            <div className="absolute top-6 left-5 flex flex-col">
              <div className="text-17 text-gray font-bold">{name.brandName}</div>
              <div className="text-17 text-gray/50 font-medium">{name.modelName}</div>
            </div>
            <img
              src={VehicleCdn.getVehicleBrandImage(name.brand)}
              className="absolute top-6 right-5 h-[2.625rem]"
              alt={name.brandName}
            />
            <div className="absolute bottom-5 left-5 flex items-center gap-1.5 text-inverted">
              <FuelTypeLabel fuelType={fuel.type} />
              <div className="text-15 font-bold">
                {fuel.amount} / {fuel.capacity}
              </div>
            </div>
            {status !== VehicleStatus.Pending && (
              <div
                className={clsx(
                  "absolute right-5 bottom-5 text-10 font-bold text-gray z-1",
                  "bg-gradient-to-l from-gray/50 via-50% via-gray/50 to-accent-muted/30 py-2 px-2.5 rounded-[0.375rem] backdrop-blur-[2px]",
                )}
              >
                {t(`estate.vehicles.status.${status.toLowerCase()}`)}
              </div>
            )}
            <div
              className="relative w-[16.25rem] h-[9.1875rem]"
              style={
                {
                  "--brand-color": "var(--accent-solid)",
                } as unknown as React.CSSProperties
              }
            >
              <img
                src={VehicleCdn.getVehicleImage(model)}
                alt=""
                className="absolute w-full h-full z-1"
              />
              <VehicleStand className="absolute bottom-0" />
            </div>
          </div>
          <div className="flex flex-col gap-4 p-5 flex-grow h-full bg-gray/10">
            <div className="text-12 font-bold text-white/50 uppercase">
              {t("estate.specifications")}:
            </div>
            <div>
              <div className="flex justify-between items-center">
                <div className="text-16 font-medium text-white">{t("estate.speed")}</div>
                <div className="text-15 font-medium text-white">
                  {specification.speed}
                  {t("estate.vehicles.kmH")}
                </div>
              </div>
              <ProgressBar percent={speedPercent} className="mt-4" />
            </div>

            <div>
              <div className="text-16 font-medium text-white">{t("estate.control")}</div>
              <ProgressBar percent={handlingPercent} className="mt-4" />
            </div>

            <div>
              <div className="text-16 font-medium text-white">{t("estate.acceleration")}</div>
              <ProgressBar percent={accelerationPercent} className="mt-4" />
            </div>
            <div className="flex gap-2 mt-1">
              <div className="flex flex-1 flex-col gap-0.5 h-14 py-3 px-3.5 bg-[#1A1A1A] rounded-xl">
                <p className="text-14 font-bold">{plateNumber}</p>
                <p className="text-11 font-medium uppercase">{t("estate.vehicles.number")}</p>
              </div>
              <div className="flex flex-1 flex-col gap-0.5 h-14 py-3 px-3.5 bg-[#1A1A1A] rounded-xl">
                <p className="text-14 font-bold">{mileage}</p>
                <p className="text-11 font-medium uppercase">{t("estate.vehicles.mileage")}</p>
              </div>
              <div className="flex flex-1 flex-col gap-0.5 h-14 py-3 px-3.5 bg-[#1A1A1A] rounded-xl">
                <p className="text-14 font-bold">{condition}%</p>
                <p className="text-11 font-medium uppercase">{t("estate.vehicles.condition")}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-[1px] mt-[1px] w-full rounded-b-2xl overflow-clip">
          <div className="flex justify-between items-center w-[24rem] p-5 bg-dark-800/50">
            <p className="text-11 font-bold text-white/50 uppercase">
              {t("estate.vehicles.trunkCapacity")}:
            </p>
            <p className="text-12 font-bold">
              <span className="text-accent-solid">{maxInventoryWeight}</span> KG
            </p>
          </div>
          <div className="flex justify-between items-center grow p-5 bg-dark-800/50">
            <p className="text-11 font-bold text-white/50 uppercase">{t("estate.vehicles.vin")}:</p>
            <p className="text-12 font-bold uppercase">{vinNumber}</p>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-3.5">
        <p className="text-12 font-bold text-gray/50 uppercase">
          {t("estate.vehicles.upgrade.title")}:
        </p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(upgrade).map(([key, value]) => (
            <div
              key={key}
              className="flex justify-between items-center w-[calc(50%-0.25rem)] h-11 py-[0.6875] px-3.5 bg-dark-800/50 border border-dashed border-[#1A1A1A] rounded-xl"
            >
              <div className="flex items-center gap-2.5">
                {upgradeIcon[key]?.({ size: 22 })}
                <p className="text-12 font-bold text-gray/50 uppercase">
                  {t(`estate.vehicles.upgrade.${key}`)}
                </p>
              </div>
              <p className="text-12 font-bold text-accent-solid uppercase">
                {value} {t("estate.vehicles.upgrade.level")}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-3.5">
        <div className="text-12 font-bold text-gray/50 uppercase">
          {t("estate.vehicles.owner.title")}
        </div>
        {owner.type === VehicleOwner.Character && (
          <PersonalDataCard
            id={owner.content.characterId}
            name={owner.content.name}
            isOnline={!!owner.content.playerMpId}
          />
        )}
      </div>

      {shared.length > 0 && (
        <div className="flex flex-col gap-5">
          <div className="text-12 font-bold text-white/50 uppercase">
            {t("estate.transportKeys")}:
          </div>
          <div className="flex flex-col [&>*:not(:last-child)]:border-b-[1px] [&>*:not(:last-child)]:border-dark-800">
            {shared.map((item) => (
              <div className="flex items-center justify-between h-[3.5rem]" key={item.characterId}>
                <div className="text-16 font-medium text-white">{item.name}</div>
                {relation.isOwner && !isDetaching && (
                  <Button
                    variant="soft"
                    color="gray"
                    className="p-0 w-[1.75rem] h-[1.75rem] shrink-0"
                    onClick={() => onCharacterDetach(vehicleId, item.characterId)}
                  >
                    <CrossIcon size={10} />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
